import React, { useEffect } from "react";
import { Button, downloadFile, Modal, Table, Typography, useAlerts, useLoading } from "best-common-react";
import { EmailListRow, EmailListUploadSummary } from "../types/emailList.ts";
import { downloadEmailListSummaryExport, getEmailListSummary } from "../api/AuthenticatedClient.ts";
import styled from "styled-components";
import { formatCreatedAt } from "../util/DateUtil.ts";

const BodyContainer = styled.div`
  min-height: 200px;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

type EmailListUploadSummaryModalProps = {
  show: boolean;
  emailListRow?: EmailListRow;
  onClose: () => void;
};

const EmailListUploadSummaryModal: React.FC<EmailListUploadSummaryModalProps> = ({ show, emailListRow, onClose }) => {
  const { setLoading } = useLoading();
  const { addAlert } = useAlerts();
  const [summary, setSummary] = React.useState<EmailListUploadSummary>();

  useEffect(() => {
    if (emailListRow) {
      setLoading(true);
      getEmailListSummary(emailListRow.id as number)
        .then(setSummary)
        .finally(() => setLoading(false));
    } else {
      setSummary(undefined);
    }
  }, [emailListRow]);

  useEffect(() => {
    if (!show) {
      setSummary(undefined);
    }
  }, [show]);

  const onExport = async () => {
    try {
      setLoading(true);
      const response = await downloadEmailListSummaryExport(emailListRow?.id as number);
      downloadFile(response);
    } catch (e) {
      // @ts-ignore
      if (e.status === 404) {
        addAlert({
          text: "Email List Upload Summary Export Not Found",
          type: "danger"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} size="lg">
      <Modal.Header>
        <HeaderContainer>
          <Typography variant="h5"> {emailListRow?.name}</Typography>
          <Typography variant="body1" className="m-0">
            Status: {emailListRow?.status}
          </Typography>
          <Typography variant="body1" className="m-0">
            Upload by {emailListRow?.createdBy} on {formatCreatedAt(emailListRow?.createdAt)}
          </Typography>
        </HeaderContainer>
      </Modal.Header>
      <Modal.Body styles={{ background: "white" }}>
        <BodyContainer>
          <Table>
            <Table.Head>
              <Table.Header>Summary Report</Table.Header>
              <Table.Header />
            </Table.Head>
            <Table.Body>
              {summary?.summaryReport.map(({ key, value }) => (
                <Table.Row key={key}>
                  <Table.Td>{key}</Table.Td>
                  <Table.Td>{value}</Table.Td>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </BodyContainer>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" className="me-2" onClick={onClose}>
          Close
        </Button>
        <Button variant="primary" onClick={onExport} disabled={!summary?.hasExport}>
          Export
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EmailListUploadSummaryModal;
